import { notification } from "antd"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { bulkCopyJob, BulkCopyJobRequest, getBulkCopyJobStatus, getCustomerProperties, getJobById, getJobCopies, getJobDetails, updateJobDetails, UpdateJobDetailsRequest } from "./jobsApi"

export const useCustomerPropertiesQuery = () => {
  return useQuery(['customerProperties'], {
    queryFn: () => getCustomerProperties()
  })
}

export const useBulkCopyJobMutation = (onSuccess: (batchId: string) => void) => {
  return useMutation({
    mutationFn: (request: BulkCopyJobRequest) => bulkCopyJob(request),
    onSuccess,
    onError: () => {
      const errorMessage = 'An error occurred while trying to copy the job.'
      notification.error({ message: errorMessage })
    }
  })
}

export const useBulkCopyJobStatusQuery = (batchId: string, isEnabled: boolean) => {
  return useQuery(['bulkCopyJobStatus', batchId], {
    queryFn: () => getBulkCopyJobStatus(batchId),
    refetchInterval: 5000,
    enabled: isEnabled
  })
}

export const useJobCopiesQuery = (jobId: number, isEnabled: boolean) => {
  return useQuery(['jobCopies', jobId], {
    queryFn: () => getJobCopies(jobId),
    enabled: isEnabled
  })
}

export const useGetJobByIdQuery = (jobId: number) => {
  return useQuery(['job', jobId], {
    queryFn: () => getJobById(jobId),
    refetchOnWindowFocus: false
  })
}

export const useJobDetailsQuery = (jobId: number) => {
  return useQuery(['jobDetails', jobId], {
    queryFn: () => getJobDetails(jobId),
    refetchOnWindowFocus: false
  })
}

export const useUpdateJobDetailsMutation = (jobId: number, onSuccess?: (request: UpdateJobDetailsRequest) => void) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: UpdateJobDetailsRequest) => updateJobDetails(jobId, request),
    onSuccess: (_data, request) => {
      queryClient.invalidateQueries(['jobDetails', jobId])
      onSuccess?.(request)
    },
    onError: () => {
      notification.error({ message: 'An error occurred while saving job details.' })
    }
  })
}
export const useJobDetailsInvalidator = () => {
  const client = useQueryClient()
  return { invalidateForJob: (jobId: number) => Promise.all([
    client.invalidateQueries(['jobDetails', jobId]),
    client.invalidateQueries(['job', jobId]),
  ]) }
}
